import type { MapRef } from 'react-map-gl/maplibre';
import { Map, LngLatLike, GeoJSONSource, LngLat } from 'maplibre-gl';
import { MutableRefObject } from 'react';
import { FocusedPlace, emptyCoordinates, Coordinates, PendingSlapList, Board, emptyBounds } from '../utils/Primitives';
import { getPlace } from '../places/Places';

export const actionButtonLayerId = `lfi-action-button`;

export type DisplayLastFocusedPlaceProps = {
  boardRef: MutableRefObject<Board>;
  mapRef?: MutableRefObject<MapRef | null>;
  lastFocusedPlace: FocusedPlace | null;
  slapsInProgress: PendingSlapList | null;
};

export const renderLastFocusedPlace = (params: DisplayLastFocusedPlaceProps) => {
    const { mapRef, boardRef, lastFocusedPlace, slapsInProgress } = params;

    if (!mapRef || !mapRef?.current) return;
    const map: Map = mapRef.current.getMap();
    if (!map) return;

    const isFocusedPlace = lastFocusedPlace?.placeId !== null;
    const isNone = !isFocusedPlace;
    let place;
    let placeBounds;
    if (lastFocusedPlace?.placeId) {
        place = getPlace({ boardRef: boardRef, placeId: lastFocusedPlace.placeId });
        if (place) {
            placeBounds = place.bounds;
        }
    }
    if (!placeBounds) {
        placeBounds = emptyBounds;
    }


    // Check if the focused item is within the current viewport
    const viewport = map.getBounds();
    const isVisible = isFocusedPlace && viewport.contains(placeBounds.getCenter());

    // If not visible, center the map on the focused item
    if (!isVisible && isFocusedPlace) {
        const center = placeBounds.getCenter();
        map.setCenter([center.lng, center.lat]);
    }

    // check if focus is on a pending slap
    let pendingSlapIsFocused = false;
    if (slapsInProgress && isFocusedPlace) {
        if (slapsInProgress.some(obj => obj.place.id == lastFocusedPlace?.placeId)) {
            pendingSlapIsFocused = true;
        }
    }

    let hideActionButton = pendingSlapIsFocused;

    // Calculate coordinates for the border
    const ne: LngLatLike = placeBounds.getNorthEast();
    const nw: LngLatLike = placeBounds.getNorthWest();
    const sw: LngLatLike = placeBounds.getSouthWest();
    const se: LngLatLike = placeBounds.getSouthEast();

    // add bounds fill source & layer
    const boundsFillSourceId = `lfi-bounds-fill-src`;

    const boundsPerimeterPoints = [[
        [ne.lng, ne.lat],
        [nw.lng, nw.lat],
        [sw.lng, sw.lat],
        [se.lng, se.lat],
        [ne.lng, ne.lat],
    ]];

    const boundsFillSrc = map.getSource(boundsFillSourceId) as GeoJSONSource | undefined;
    if (boundsFillSrc == undefined) {
        map.addSource(boundsFillSourceId, {
            'type': 'geojson',
            'data': {
                type: 'Polygon',
                coordinates: [[

                ]]
        }});
    } else {
        boundsFillSrc.setData({
            type: 'Polygon',
            coordinates: boundsPerimeterPoints
        });
    }
    const boundsFillLayerId = `lfi-bounds-fill`;
    const boundsOutlineLayerId = `lfi-bounds-outline`;
    const boundsFillLayer = map.getLayer(boundsFillLayerId);
    if (boundsFillLayer == undefined) {
        map.addLayer({
            id: boundsFillLayerId,
            type: 'fill',
            source: boundsFillSourceId,
            paint: {
                'fill-color': '#d6c400',
                'fill-opacity': 0.2,
            }
     });
    } else {
        if (isNone) {
            map.setLayoutProperty(boundsFillLayerId, 'visibility', 'none');
        } else {
            map.setLayoutProperty(boundsFillLayerId, 'visibility', 'visible');
        }
    }
    const boundsOutlineLayer = map.getLayer(boundsOutlineLayerId);
    if (boundsOutlineLayer == undefined) {
        map.addLayer({
            id: boundsOutlineLayerId,
            type: 'line',
            source: boundsFillSourceId,
            paint: {
                'line-color': '#b59a00',
                'line-width': 1,
                'line-opacity': 0.6,
            }
        });
    } else {
        if (isNone) {
            map.setLayoutProperty(boundsOutlineLayerId, 'visibility', 'none');
        } else {
            map.setLayoutProperty(boundsOutlineLayerId, 'visibility', 'visible');
        }
    }
    map.moveLayer(boundsFillLayerId);
    map.moveLayer(boundsOutlineLayerId);

    const slapHereButtonImageUrl = '/img/ui/slap-here-button-p1.png';
    const boundsCenter: LngLat = placeBounds.getCenter();
    const buttonHeight = 0.0033;
    const buttonWidth = 0.0085;
    const buttonHalfHeight = buttonHeight / 2;
    const buttonHalfWidth = buttonWidth / 2;
    const actionButtonCoordinates: Coordinates = [
        new LngLat(boundsCenter.lng - buttonHalfWidth, boundsCenter.lat + buttonHalfHeight),
        new LngLat(boundsCenter.lng + buttonHalfWidth, boundsCenter.lat + buttonHalfHeight),
        new LngLat(boundsCenter.lng + buttonHalfWidth, boundsCenter.lat - buttonHalfHeight),
        new LngLat(boundsCenter.lng - buttonHalfWidth, boundsCenter.lat - buttonHalfHeight),
    ];

    /* Temporarily commented out - evaluating UI without map action button
    const slapHereButtonImageSourceId = `lfi-crew-image-src`;
    const slapHereButtonImageSource = map.getSource(slapHereButtonImageSourceId) as ImageSource | undefined;
    if (slapHereButtonImageSource == undefined) {
        map.addSource(slapHereButtonImageSourceId, {
            'type': 'image',
            'url': slapHereButtonImageUrl,
            'coordinates':  [
                [actionButtonCoordinates[0].lng, actionButtonCoordinates[0].lat],
                [actionButtonCoordinates[1].lng, actionButtonCoordinates[1].lat],
                [actionButtonCoordinates[2].lng, actionButtonCoordinates[2].lat],
                [actionButtonCoordinates[3].lng, actionButtonCoordinates[3].lat],
            ]
        });
    } else {
        slapHereButtonImageSource.updateImage({
            'url': slapHereButtonImageUrl,
            coordinates: [
            [actionButtonCoordinates[0].lng, actionButtonCoordinates[0].lat],
            [actionButtonCoordinates[1].lng, actionButtonCoordinates[1].lat],
            [actionButtonCoordinates[2].lng, actionButtonCoordinates[2].lat],
            [actionButtonCoordinates[3].lng, actionButtonCoordinates[3].lat],
        ] });
    }
    */

    /* Temporarily commented out - evaluating UI without map action button
    const actionButtonLayer = map.getLayer(actionButtonLayerId);
    if (actionButtonLayer == undefined) {
        map.addLayer({
            id: actionButtonLayerId,
            type: 'raster',
            source: slapHereButtonImageSourceId
        });
    } else {
        if (hideActionButton || isNone) {
            map.setLayoutProperty(actionButtonLayerId, 'visibility', 'none');
        } else {
            map.setLayoutProperty(actionButtonLayerId, 'visibility', 'visible');
        }
    }
    map.moveLayer(actionButtonLayerId);
    */

    // Temporarily return empty coordinates since action button is disabled
    return emptyCoordinates;
    // return hideActionButton ? emptyCoordinates : actionButtonCoordinates;
}
